/**
 * Google Maps API enum constants
 *
 * These enums match the Google Maps API enums and can be used without loading
 * the Google Maps library, avoiding the need to call `useGoogleMaps()` in files
 * that only need enum values.
 *
 * For files that need to instantiate Google Maps objects (Map, Marker, Geocoder, etc.),
 * use the `useGoogleMaps()` hook instead.
 */

/**
 * Status values returned by PlacesService operations
 * @see https://developers.google.com/maps/documentation/javascript/reference/places-service#PlacesServiceStatus
 */
export enum PlacesServiceStatus {
  /**
   * The response contains a valid result.
   */
  OK = 'OK',
  /**
   * No result was found for this request.
   */
  ZERO_RESULTS = 'ZERO_RESULTS',
  /**
   * The application has gone over its request quota.
   */
  OVER_QUERY_LIMIT = 'OVER_QUERY_LIMIT',
  /**
   * The application is not allowed to use the <code>PlacesService</code>.
   */
  REQUEST_DENIED = 'REQUEST_DENIED',
  /**
   * This request was invalid.
   */
  INVALID_REQUEST = 'INVALID_REQUEST',
  /**
   * The <code>PlacesService</code> request could not be processed due to a
   * server error. The request may succeed if you try again.
   */
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Ranking method for nearby search results
 * @see https://developers.google.com/maps/documentation/javascript/reference/places-service#RankBy
 */
export enum RankBy {
  /**
   * Ranks place results by distance from the location.
   */
  PROMINENCE = 0.0,
  /**
   * Ranks place results by their prominence.
   */
  DISTANCE = 1.0,
}

/**
 * Business status of a place
 * @see https://developers.google.com/maps/documentation/javascript/reference/places-service#BusinessStatus
 */
export enum BusinessStatus {
  /**
   * The business is operating normally..
   */
  OPERATIONAL = 'OPERATIONAL',
  /**
   * The business is closed temporarily.
   */
  CLOSED_TEMPORARILY = 'CLOSED_TEMPORARILY',
  /**
   * The business is closed permanently.
   */
  CLOSED_PERMANENTLY = 'CLOSED_PERMANENTLY',
}

/**
 * Travel modes for directions requests
 * @see https://developers.google.com/maps/documentation/javascript/reference/directions#TravelMode
 */
export enum TravelMode {
  /**
   * Specifies a driving directions request.
   */
  DRIVING = 'DRIVING',
  /**
   * Specifies a walking directions request.
   */
  WALKING = 'WALKING',
  /**
   * Specifies a bicycling directions request.
   */
  BICYCLING = 'BICYCLING',
  /**
   * Specifies a transit directions request.
   */
  TRANSIT = 'TRANSIT',
}

/**
 * Status values returned by DirectionsService
 * @see https://developers.google.com/maps/documentation/javascript/reference/directions#DirectionsStatus
 */
export enum DirectionsStatus {
  /**
   * The response contains a valid <code>DirectionsResult</code>.
   */
  OK = 'OK',
  /**
   * At least one of the origin, destination, or waypoints could not be
   * geocoded.
   */
  NOT_FOUND = 'NOT_FOUND',
  /**
   * No route could be found between the origin and destination.
   */
  ZERO_RESULTS = 'ZERO_RESULTS',
  /**
   * Too many <code>DirectionsWaypoint</code>s were provided in the
   * <code>DirectionsRequest</code>. See the <a
   * href="https://developers.google.com/maps/documentation/javascript/directions#UsageLimits">
   * developer&#39;s guide</a> for the maximum number of waypoints allowed.
   */
  MAX_WAYPOINTS_EXCEEDED = 'MAX_WAYPOINTS_EXCEEDED',
  /**
   * The <code>DirectionsRequest</code> provided was invalid.
   */
  INVALID_REQUEST = 'INVALID_REQUEST',
  /**
   * The webpage has gone over the requests limit in too short a period of
   * time.
   */
  OVER_QUERY_LIMIT = 'OVER_QUERY_LIMIT',
  /**
   * The webpage is not allowed to use the directions service.
   */
  REQUEST_DENIED = 'REQUEST_DENIED',
  /**
   * A directions request could not be processed due to a server error. The
   * request may succeed if you try again.
   */
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Status values returned by Geocoder
 * @see https://developers.google.com/maps/documentation/javascript/reference/geocoder#GeocoderStatus
 */
export enum GeocoderStatus {
  OK = 'OK',
  /**
   * No result was found for this <code>GeocoderRequest</code>.
   */
  ZERO_RESULTS = 'ZERO_RESULTS',
  /**
   * The webpage has gone over the requests limit in too short a period of
   * time.
   */
  OVER_QUERY_LIMIT = 'OVER_QUERY_LIMIT',
  /**
   * The webpage is not allowed to use the geocoder.
   */
  REQUEST_DENIED = 'REQUEST_DENIED',
  /**
   * This <code>GeocoderRequest</code> was invalid.
   */
  INVALID_REQUEST = 'INVALID_REQUEST',
  /**
   * A geocoding request could not be processed due to a server error. The
   * request may succeed if you try again.
   */
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  /**
   * There was a problem contacting the Google servers.
   */
  ERROR = 'ERROR',
}
